import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { Mail, Password, Person } from '@mui/icons-material';
import ContactDetailsForm from '../../../components/forms/profile/contact-details.form';
import PasswordForm from '../../../components/forms/profile/password.form';
import { PasswordDto } from '../../../services/dtos/form/password.dto';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { PasswordCriterionsDto } from '../../../services/dtos/password-criterions.dto';
import { getCivilityById, getInterlocutorsTypesById } from '../../../services/references.service';
import {
    deactivateAccount,
    getPasswordCriterions,
    getUser,
    isValidPassword,
    updateAccount,
    updatePassword,
} from '../../../services/account.service';
import { AxiosError } from 'axios';
import { UserDto } from '../../../services/dtos/user.dto';
import PersonalInfos from '../../../components/forms/profile/personal-infos/personal-infos';
import { ContactDetailsDto } from '../../../services/dtos/contact-details.dto';
import { UpdateAccountDto } from '../../../services/dtos/update-account.dto';
import { messageinfosPassword } from '../../../utils/traitment.utils';
import { InterlocutorsReferenceDto } from '../../../services/dtos/interlocutors-reference.dto';
import { getValueLocalStorage } from '../../../hooks/use-local-storage.hook';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { useConfirm } from 'material-ui-confirm';
import { useAuth } from '../../../hooks/use-auth.hook';

//Props du panel des tabs
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

/**
 * Fonction qui crée les tabs contenu
 * @param props propriétés
 * @constructor
 */
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

/**
 * Permet de définir sur quel tabs on est.
 * @param index tab id
 */
function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

/**
 * Composant de la page du profil utilisateur
 * @constructor
 */
export default function ProfilePage() {
    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    //Use confirm dialog
    const confirm = useConfirm();
    // Use auth
    const auth = useAuth();
    //Position des tabs
    const tabProfile = 0;
    const tabContactDetails = 1;
    const tabPassword = 2;
    const user = getValueLocalStorage('user');

    // Détection du mode RU (Responsable Usager)
    const isRU = !!(user && user.idLoggedAs);

    //Ici sont présentes tous les useStates
    const [civility, setCivility] = useState<ReferenceDto>();
    const [interlocutor, setInterlocutor] = useState<InterlocutorsReferenceDto>();
    const [currentUser, setCurrentUser] = useState<UserDto>();
    const [valueTab, setValueTab] = React.useState(0);
    const [contactFormSend, setContactFormSend] = React.useState<boolean>(false);
    const [passwordFormSend, setPasswordFormSend] = React.useState<boolean>(false);

    const [messagesPassword, setMessagesPassword] = useState<string[]>([]);

    //Gestion du mot de passe
    const [passwordCriterions, setPasswordCriterions] = useState<PasswordCriterionsDto | null>(null);

    useEffect(() => {
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;

        //Appel des critères pour les mots de passe
        getPasswordCriterions()
            .then(result => {
                //Ajout le message pour la majuscule
                if (result.upperCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en majuscule']);
                }
                //Ajout le message pour la minuscule
                if (result.lowerCase) {
                    setMessagesPassword(oldArray => [...oldArray, 'une lettre en minuscule']);
                }
                //Ajout le message pour le chiffre
                if (result.number) {
                    setMessagesPassword(oldArray => [...oldArray, 'un chiffre']);
                }
                //Ajout le message pour le caractère spécifique
                if (result.specialChars) {
                    setMessagesPassword(oldArray => [...oldArray, 'un caractère spécifique']);
                }
                //Ajout le message pour la taille
                if (result.maxLength !== undefined && result.minLength !== undefined) {
                    setMessagesPassword(oldArray => [
                        ...oldArray,
                        'au minimum ' + result.minLength + ' caractères et maximum ' + result.maxLength,
                    ]);
                }
                setPasswordCriterions(result);
            })
            .catch(error => {
                showAxiosMessageError(error, showMessage);
            });

        //Récupération de l'utilisateur courant
        getInfosUser();

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Récupération de la civilité
        //Pour pouvoir mapper les infos de la personne
        if (currentUser) {
            const civilityId = currentUser?.civilite?.split('/').pop();
            if (civilityId !== undefined) {
                getCivilityById(civilityId)
                    .then(result => {
                        const data: ReferenceDto = {
                            value: result.id,
                            label: result.nom,
                        };
                        setCivility(data);
                    })
                    .catch(error => {
                        showAxiosMessageError(error, showMessage);
                    });
            }

            //Récupération du types d'interlocuteurs
            //Pour pouvoir mapper les infos de la personne
            const typeInterlocutorsId = currentUser?.type?.split('/').pop();
            if (typeInterlocutorsId !== undefined) {
                getInterlocutorsTypesById(typeInterlocutorsId)
                    .then(result => {
                        const data: InterlocutorsReferenceDto = {
                            id: result.id,
                            label: result.nom,
                            moralPerson: result.personneMorale,
                        };
                        setInterlocutor(data);
                    })
                    .catch(error => {
                        console.error("Erreur lors de la récupération des types d'interlocuteurs");
                        showAxiosMessageError(error, showMessage);
                    });
            }
        }

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    //Handler qui permet de gérer les changements sur le champ "mot de passe"
    const handlerOnChangePassword = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        e.preventDefault();
        if (e?.target?.value !== undefined && e?.target?.value !== '' && e?.target?.value.length > 0) {
            //Appel du web service validation de mot de passe
            isValidPassword(e?.target?.value)
                .then(() => {
                    showMessage.infoAlert('Mot de passe validé');
                })
                .catch((error: Error | AxiosError) => {
                    showAxiosMessageError(error, showMessage);
                });
        }
    };

    //Permet de gérer sur quelle vue de tab on est.
    const handleChangeValueTab = (event: React.SyntheticEvent, newValue: number) => {
        setValueTab(newValue);
    };

    // Comportement au submit du formulaire contact détails
    const handlerSubmitContactDetails = (e: ContactDetailsDto) => {
        setContactFormSend(true);

        //Mapping des données
        const updateAccountDto: UpdateAccountDto = {
            utilisateur: e,
        };

        //Appel à la modification de compte
        updateAccount(updateAccountDto, isRU ? user.idLoggedAs : user.userId)
            .then(() => {
                showMessage.successAlert('Votre compte a été mis à jour avec succès');
                getInfosUser();
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setContactFormSend(false);
            });
    };

    // Comportement au submit du formulaire mot de passe
    const handlerSubmitPassword = (e: PasswordDto) => {
        setPasswordFormSend(true);

        //Appel au web service de modificationd e mot de passe
        updatePassword(e)
            .then(result => {
                showMessage.successAlert(result.message);
            })
            .catch((error: Error | AxiosError) => {
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setPasswordFormSend(false);
            });
    };

    /**
     * Function to handle the deactivation of a user account.
     *
     * @returns {void}
     */
    const handleClickDeactivateAccount = () => {
        if (user.userId !== undefined) {
            confirm({
                title: 'Êtes-vous certain de vouloir effectuer cette action ?',
                content: "Ceci désactivera votre compte sur l'agence en ligne",
                cancellationText: 'Annuler',
            })
                .then(() => {
                    deactivateAccount(user.userId)
                        .then(() => {
                            showMessage.successAlert('Le compte a bien été désactivé.');
                            auth.signout();
                        })
                        .catch(error => {
                            console.error('Erreur lors de la désactivation du compte');
                            showAxiosMessageError(error, showMessage);
                        });
                })
                .catch(() => console.warn('Désactivation du compte annulée.'));
        }
    };

    return (
        //Affichage de tabs
        //Gestion des contenus des différents tabs
        <>
            <Typography variant={'h4'}>Mon Profil</Typography>
            <Tabs
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                value={valueTab}
                onChange={handleChangeValueTab}
                sx={{ marginBottom: '10px' }}
            >
                <Tab icon={<Person />} label="Infos personnelles" {...a11yProps(tabProfile)} />
                <Tab icon={<Mail />} label="Coordonnées" {...a11yProps(tabContactDetails)} />
                <Tab icon={<Password />} label="Identifiants de connexion" {...a11yProps(tabPassword)} />
            </Tabs>
            <TabPanel value={valueTab} index={0}>
                {currentUser ? (
                    <PersonalInfos
                        civility={civility}
                        interlocutor={interlocutor}
                        user={currentUser}
                        handleClickDeactivateAccount={handleClickDeactivateAccount}
                    />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>
            <TabPanel value={valueTab} index={1}>
                {currentUser ? (
                    <ContactDetailsForm
                        handlerSubmit={handlerSubmitContactDetails}
                        user={currentUser}
                        contactFormSend={contactFormSend}
                    />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>
            <TabPanel value={valueTab} index={2}>
                {currentUser ? (
                    <PasswordForm
                        user={currentUser}
                        handlerSubmit={handlerSubmitPassword}
                        passwordCriterions={passwordCriterions}
                        infosPassword={messageinfosPassword(messagesPassword)}
                        handlerOnChangePassword={handlerOnChangePassword}
                        passwordFormSend={passwordFormSend}
                        isRU={isRU}
                    />
                ) : (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                )}
            </TabPanel>
        </>
    );

    /**
     * Méthode qui récupère les infos de la personne
     */
    function getInfosUser() {
        //Récupération de l'utilisateur courant
        user &&
            getUser(user.idLoggedAs ? user.idLoggedAs : user.userId)
                .then(result => {
                    setCurrentUser(result);
                })
                .catch(error => {
                    console.error("Erreur lors de la récupération de l'utilisateur");
                    showAxiosMessageError(error, showMessage);
                });
    }
}
